import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { consoleLogError } from "@/shared/utils/console";
import { libraryActions, swActionConstants, swActions, systemActions } from "@/store/actions";
import {
  postSWMessageCacheAudioFile,
  postSWMessageCacheBook,
  postSWMessageCacheUrls,
  postSWMessageCacheWaveform,
  postSWMessageCancelProcess, postSWMessageGetAudioFile,
  postSWMessageGetBook,
  postSWMessageGetInitData, postSWMessageGetWaveform
} from "@/shared/utils/sw/sw";
import { fetchDownloadBook } from "@/shared/api/content/books/requests";
import { addQuotes } from "@/shared/utils/string/string";
import {
  CUSTOM_EVENT_TYPE_NEW_PULLED_AUDIO_FILE_SRC,
  ID_PROCESS_CACHE_URLS,
  NAMED_CACHE_NAME, STORAGE_KEY_IS_USER_CANCELED_AUTO_DOWNLOADING
} from "@/shared/utils/sw/sw.constants";
import { fetchAudioFileAsBase64 } from "@/shared/api/content/audio/requests";
import { fetchAndStoreWaveformsDictionary } from "@/shared/store/saga-functions";
import { makeWaveformsJsonUrl } from "@/shared/utils/url/url";
import { setLocalStorageItem } from "@/shared/utils/storage/storage";
import { _sharedActionsMainTree } from "@/shared/store/mainTree/actions";
import { _sharedActionsPlayer } from "@/shared/store/player/actions";
import { dispatchCustomEvent } from "@/shared/utils/dom";
import { _sharedActionsToc } from "@/shared/store/toc/actions";
import { _sharedActionsParagraphs } from "@/shared/store/paragraphs/actions";
import { PROGRESS_VALUE_REMOVE } from "@/shared/store/sw/constants";
import {
  makeAudioDownloadCancelTokenKey,
  makeBooksDownloadCancelTokenKey
} from "@/shared/api/content/books/constants";
import { cancelRequest } from "@/shared/api/utils";
import {
  selectorWebsiteUrlsForOfflineUse
} from "@/shared/store/sw/selectorWebsiteUrlsForOfflineUse";
import { selectorIsDownloadingWebsite } from "@/shared/store/sw/selectorIsDownloadingWebsite";
import {
  selectorDownloadAudiosProgresses
} from "@/shared/store/sw/selectorDownloadAudiosProgresses";
import { selectorAudiosDownloadedIds } from "@/shared/store/sw/selectorAudiosDownloadedIds";
import { selectorDownloadBooksProgresses } from "@/shared/store/sw/selectorDownloadBooksProgresses";
import { selectorDownloadedBooksIds } from "@/shared/store/sw/selectorDownloadedBooksIds";

const TAG = "SW";

/* ===== WEBSITE ===== */
function* downloadWebsite() {
  const urlsForOfflineUse = yield select(selectorWebsiteUrlsForOfflineUse);
  const isDownloadingWebsite = yield select(selectorIsDownloadingWebsite);

  if (isDownloadingWebsite) {
    postSWMessageCancelProcess(ID_PROCESS_CACHE_URLS);
  }

  yield call(postSWMessageCacheUrls, urlsForOfflineUse);
}

function* cancelDownloadWebsite() {
  const isUrlsDownloading = yield select(selectorIsDownloadingWebsite);

  if (isUrlsDownloading) {
    postSWMessageCancelProcess(ID_PROCESS_CACHE_URLS);
    setLocalStorageItem(STORAGE_KEY_IS_USER_CANCELED_AUTO_DOWNLOADING, "1");
  }
}
/* ==================== */

/* ======= NAMED CACHE ======= */
function* _downloadDataWorker(
  fetch,
  actionUpdateProgress,
  textSuccess,
  textCancel,
  textNoData,
  postMessage,
) {
  let canceled = false;
  const data = yield call(fetch, (progress) => {
    // FIXME: possibly lame approach
    if (global.window) {
      global.window.store.dispatch(actionUpdateProgress(progress.loaded / progress.total));
    }
  }, () => {
    canceled = true;
  });

  yield put(actionUpdateProgress(PROGRESS_VALUE_REMOVE));

  if (canceled) {
    yield put(systemActions.toast(textCancel));
    return false;
  }

  if (!data) {
    yield put(systemActions.toast(textNoData));
    return false;
  }

  postMessage(data);

  yield put(systemActions.toast(textSuccess));
  return true;
}

/* ==== BOOK ==== */
const makeToastMessageCancelBook = (book) => {
  return ["@sw:downloadCancelled", " " + addQuotes(book.title)];
};

function* _downloadBook(book) {
  const nBookId = book.book_id;

  const isSuccess = yield call(
    _downloadDataWorker,
    (onProgress, onCancel) => fetchDownloadBook(book, onProgress, onCancel),
    (progress) => swActions.updateDownloadProgressBookData(nBookId, progress),
    [addQuotes(book.title) + " ", "@sw:successfullyDownloaded", "!"],
    makeToastMessageCancelBook(book),
    "@sw:bookNotAvailableOffline",
    (data) => postSWMessageCacheBook(nBookId, data),
  );

  return isSuccess;
}

function* downloadBooks(action) {
  const { data: books, onSuccess } = action.data;
  let downloadSuccessCount = 0;
  try {
    const downloadedBooksIds = yield select(selectorDownloadedBooksIds);
    const downloadingBooksProgresses = yield select(selectorDownloadBooksProgresses);
    const booksNotDownloaded = [];

    // Filter. Leaves only not downloaded/downloading books.
    let book, bookId;
    for (let i = 0; i < books.length; i++) {
      book = books[i];
      bookId = book.book_id.toString();
      if (
        !downloadedBooksIds.includes(bookId) && downloadingBooksProgresses[bookId] === undefined
      ) {
        booksNotDownloaded.push(book);
        // Initiate progresses, so user will see how many books are in the downloading queue
        yield put(swActions.updateDownloadProgressBookData(bookId, 0));
      }
    }
    // ================================================================================

    let downloadingBooksProgressesReselected;
    for (let i = 0; i < booksNotDownloaded.length; i++) {
      downloadingBooksProgressesReselected = yield select(selectorDownloadBooksProgresses);
      if (
        // If an item has no value of progress, then consider its downloading had been canceled.
        // It's because the progress, but not the request is init beforehand,
        // so, on click cancel btn, there is no request to cancel.
        downloadingBooksProgressesReselected[booksNotDownloaded[i].book_id] !== undefined
        && (yield call(_downloadBook, booksNotDownloaded[i]))
      ) {
        downloadSuccessCount++;
      }
    }
  } catch (error) {
    consoleLogError(TAG, "Cache book for offline use", error);
  } finally {
    if (downloadSuccessCount) {
      if (onSuccess) {
        onSuccess();
      }
    }
  }
}

/* ==== AUDIO ==== */
const makeToastMessageCancelAudio = (audioChapter) => {
  return ["@sw:downloadCancelled", " " + addQuotes(audioChapter.title)];
};

function* _downloadAudio(audioChapter) {
  const id = audioChapter.para_id;

  const isSuccess = yield call(
    _downloadDataWorker,
    (onProgress, onCancel) => fetchAudioFileAsBase64(id, audioChapter.mp3, onProgress, onCancel),
    (progress) => swActions.updateDownloadProgressAudio(id, progress),
    [addQuotes(audioChapter.title) + " ", "@sw:successfullyDownloaded", "!"],
    makeToastMessageCancelAudio(audioChapter),
    ["@sw:noInternetConnection", ". ", "@sw:featureNotAvailable", "."],
    (data) => postSWMessageCacheAudioFile(id, "data:audio/mpeg;base64," + data),
  );

  // Fetch waveform too. It's easier to fetch it with the audio file for now.
  if (isSuccess && audioChapter.mp3) {
    const waveform = yield fetchAndStoreWaveformsDictionary(
      makeWaveformsJsonUrl(audioChapter.mp3), id
    );
    postSWMessageCacheWaveform(id, waveform);
  }
  // ===================

  return isSuccess;
}

function* downloadAudioChapters(action) {
  const items = action.data.data;
  try {
    const downloadedIds = yield select(selectorAudiosDownloadedIds);
    const downloadingProgresses = yield select(selectorDownloadAudiosProgresses);
    const itemsNotDownloaded = [];

    // Filter. Leaves only not downloaded/downloading books.
    let item, itemId;
    for (let i = 0; i < items.length; i++) {
      item = items[i];
      itemId = item.para_id;
      if (!downloadedIds.includes(itemId) && downloadingProgresses[itemId] === undefined) {
        itemsNotDownloaded.push(item);
        // Initiate progresses, so user will see how many books are in the downloading queue
        yield put(swActions.updateDownloadProgressAudio(itemId, 0));
      }
    }
    // ================================================================================

    let downloadingProgressesReselected;
    for (let i = 0; i < itemsNotDownloaded.length; i++) {
      downloadingProgressesReselected = yield select(selectorDownloadAudiosProgresses);
      if (
        // If an item has no value of progress, then consider its downloading had been canceled.
        // It's because the progress, but not the request is init beforehand,
        // so, on click cancel btn, there is no request to cancel.
        downloadingProgressesReselected[itemsNotDownloaded[i].para_id] !== undefined
      ) {
        yield call(_downloadAudio, itemsNotDownloaded[i]);
      }
    }
  } catch (error) {
    consoleLogError(TAG, "Cache AUDIO File for offline use", error);
  }
}

/* ==== COMMON ==== */
function* downloadData(action) {
  const { cacheName } = action.data;

  if (cacheName === NAMED_CACHE_NAME.BOOK_DATA) {
    yield downloadBooks(action);
  } else if (cacheName === NAMED_CACHE_NAME.AUDIO_FILE) {
    yield downloadAudioChapters(action);
  } else if (cacheName === NAMED_CACHE_NAME.WAVEFORM) {
    // for now, is downloaded with {AUDIO_FILE}.
  }
}

function* cancelDownloadData(action) {
  const { cacheName, item } = action.data;

  if (cacheName === NAMED_CACHE_NAME.BOOK_DATA) {
    const isSuchRequestWasInit = cancelRequest(makeBooksDownloadCancelTokenKey(item.book_id));
    if (!isSuchRequestWasInit) {
      yield put(systemActions.toast(makeToastMessageCancelBook(item)));
      yield put(swActions.updateDownloadProgressBookData(item.book_id, PROGRESS_VALUE_REMOVE));
    }
  } else if (cacheName === NAMED_CACHE_NAME.AUDIO_FILE) {
    const isSuchRequestWasInit = cancelRequest(makeAudioDownloadCancelTokenKey(item.para_id));
    if (!isSuchRequestWasInit) {
      yield put(systemActions.toast(makeToastMessageCancelAudio(item)));
      yield put(swActions.updateDownloadProgressAudio(item.para_id, PROGRESS_VALUE_REMOVE));
    }
  } else if (cacheName === NAMED_CACHE_NAME.WAVEFORM) {
    // for now, is canceled with {AUDIO_FILE}.
  }
}

function* pull(action) {
  const { cacheName, key } = action.data;

  if (cacheName === NAMED_CACHE_NAME.BOOK_DATA) {
    yield call(postSWMessageGetBook, key);
  } else if (cacheName === NAMED_CACHE_NAME.AUDIO_FILE) {
    yield call(postSWMessageGetAudioFile, key);
  } else if (cacheName === NAMED_CACHE_NAME.WAVEFORM) {
    yield call(postSWMessageGetWaveform, key);
  }
}

function* pullSuccess(action) {
  const { cacheName, key, value } = action.data;

  if (cacheName === NAMED_CACHE_NAME.BOOK_DATA) {
    yield put(_sharedActionsMainTree.addBooks(value.book));
    yield put(_sharedActionsToc.setItem(value.book.book_id, value.toc));
    const mapChapterNumberToParas = {};
    Object.keys(value.chaptersParas).forEach((chapterNumber) => {
      mapChapterNumberToParas[chapterNumber] = {
        paras: value.chaptersParas[chapterNumber],
        highlight: "",
      };
    });
    yield put(
      _sharedActionsParagraphs.fetchChaptersParasSuccess(
        value.book.book_id, mapChapterNumberToParas
      )
    );
  } else if (cacheName === NAMED_CACHE_NAME.AUDIO_FILE) {
    // FIXME: It's a crutch, because base64 strings are too large (e.g. 16 megabytes)!
    //  Actually, need to access the audio file directly, by using a custom endpoint
    //  in SW e.g. "/sw/named-cache/audioFile/[chapter_id]".
    //  But it's not working with mp3 file for some reason. Need to investigate!
    if (global.window) {
      dispatchCustomEvent(CUSTOM_EVENT_TYPE_NEW_PULLED_AUDIO_FILE_SRC, {
        key,
        value,
      });
    }
  } else if (cacheName === NAMED_CACHE_NAME.WAVEFORM) {
    const { waveform, paragraphsAudioPositions } = value;
    yield put(_sharedActionsPlayer.putAudioWaveformsData({
      waveform,
      paragraphsAudioPositions,
      chapterId: key,
    }));
  } else if (cacheName === NAMED_CACHE_NAME.OFFLINE_HISTORY_LISTEN) {
    const {chapterId, position, saveML, timestamp} = value;
    // Don't save on the server to prevent not obvious, for user, history updates.
    yield put(libraryActions.saveListen(
      chapterId, position, saveML, 0, timestamp, true
    ));
  } else if (cacheName === NAMED_CACHE_NAME.OFFLINE_HISTORY_READ) {
    const {chapterId, paraId, saveML, timestamp} = value;
    // Don't save on the server to prevent not obvious, for user, history updates.
    yield put(libraryActions.saveRead(
      chapterId, paraId, saveML, 0, timestamp, true
    ));
  }
}
/*  ====================  */

function* getInitStatus() {
  try {
    const urlsForOfflineUse = yield select(selectorWebsiteUrlsForOfflineUse);
    postSWMessageGetInitData(urlsForOfflineUse);
  } catch (error) {
    consoleLogError(TAG, "Get init status ERROR", error);
  }
}

export default function* swSaga() {
  yield all([
    takeLatest(swActionConstants.DOWNLOAD_WEBSITE, downloadWebsite),
    takeLatest(swActionConstants.CANCEL_DOWNLOAD_WEBSITE, cancelDownloadWebsite),
    takeEvery(swActionConstants.DOWNLOAD_DATA, downloadData),
    takeEvery(swActionConstants.CANCEL_DOWNLOAD_DATA, cancelDownloadData),
    takeEvery(swActionConstants.PULL_SUCCESS, pullSuccess),
    takeEvery(swActionConstants.PULL, pull),
    takeLatest(swActionConstants.GET_INIT_STATUS, getInitStatus),
  ]);
}
